import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Home, MessageCircle, FileCheck, Users, Youtube, Star, ExternalLink } from 'lucide-react';
import { CONTACT_INFO } from '../constants/contact';

// External links
const reviewLinks = {
  yelp: 'https://www.yelp.com/biz/the-beltas-law-firm-new-york-2',
  google: 'https://www.google.com/search?q=The+Beltas+Law+Firm+New+York+reviews',
  youtube: CONTACT_INFO.youtube,
};


const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export default function HomePage() {
  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] sm:min-h-[85vh] flex items-center bg-gradient-to-br from-[#fdfcfb] to-primary-50 overflow-hidden py-8 sm:py-0">
        {/* Decorative elements */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary-100 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-accent-100 rounded-full blur-3xl opacity-40" />

        <div className="container-main relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="max-w-xl"
            >
              <motion.p
                variants={fadeInUp}
                className="inline-block px-4 py-1.5 bg-accent-100 text-accent-700 rounded-full text-sm font-subheading font-medium mb-4"
              >
                Don't get pummeled in court — let us represent you.
              </motion.p>

              <motion.h1
                variants={fadeInUp}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-neutral-900 leading-tight tracking-tight"
              >
                Housing Law &{' '}
                <span className="text-primary-600 block sm:inline">Bankruptcy</span>
              </motion.h1>

              <motion.p
                variants={fadeInUp}
                className="mt-6 text-xl text-neutral-600 leading-relaxed"
              >
                New York City law firm focused on landlord–tenant, real estate, bankruptcy,
                collections, and consumer debt matters. Representing tenants, landlords,
                individuals, and small businesses since 2014.
              </motion.p>

              <motion.div
                variants={fadeInUp}
                className="mt-8 flex flex-col sm:flex-row gap-4"
              >
                <Link to="/contact" className="btn-primary">
                  Schedule Initial Consultation
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/about" className="btn-outline">
                  Learn About Your Rights
                </Link>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="mt-12 flex items-center gap-6 text-sm text-neutral-500"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-primary-500 rounded-full" />
                  <span>Licensed in NY, CA & DC</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-primary-500 rounded-full" />
                  <span>Initial Consultations</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:flex justify-center items-center"
            >
              <img
                src="/courtroom.jpg"
                alt="Interior of a New York courtroom"
                className="w-full max-w-md rounded-2xl shadow-2xl object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-8 sm:py-12 bg-white border-b border-neutral-100">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 sm:flex sm:flex-wrap sm:justify-center gap-4 sm:gap-8 text-center"
          >
            {[
              { label: 'Tenants', desc: 'facing eviction or rent disputes' },
              { label: 'Landlords', desc: 'dealing with nonpayment issues' },
              { label: 'Individuals', desc: 'under collection pressure' },
              { label: 'Small Businesses', desc: 'managing creditor issues' },
            ].map((item) => (
              <div key={item.label} className="sm:max-w-[220px]">
                <p className="font-subheading font-semibold text-primary-700 text-sm sm:text-base">{item.label}</p>
                <p className="text-xs sm:text-sm text-neutral-500">{item.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-semibold text-neutral-900"
            >
              How I Can Help You
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="mt-4 text-lg text-neutral-600"
            >
              Thousands of housing cases handled. Representing both landlords and tenants,
              plus bankruptcy and consumer debt across NYC and the tri-state area.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Landlord-Tenant Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="group bg-white border border-neutral-200 rounded-2xl p-8 hover:shadow-xl hover:border-primary-300 transition-all duration-300"
            >
              <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary-200 transition-colors">
                <Home className="w-7 h-7 text-primary-600" />
              </div>
              <h3 className="font-subheading text-2xl font-semibold text-neutral-900 mb-3">
                Landlord-Tenant Law
              </h3>
              <p className="text-neutral-600 mb-6 leading-relaxed">
                Representing both landlords and tenants in Manhattan, Queens, Brooklyn,
                and the Bronx. Thousands of housing cases handled since 2014.
              </p>
              <ul className="space-y-2 mb-8">
                {['Tenant representation', 'Landlord representation', 'Eviction proceedings & defense', 'Lease disputes & security deposits'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-neutral-600">
                    <div className="w-1.5 h-1.5 bg-primary-400 rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/landlord-tenant"
                className="inline-flex items-center gap-2 font-subheading font-medium text-primary-600 hover:gap-3 transition-all"
              >
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            {/* Consumer Law Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="group bg-white border border-neutral-200 rounded-2xl p-8 hover:shadow-xl hover:border-primary-300 transition-all duration-300"
            >
              <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary-200 transition-colors">
                <Shield className="w-7 h-7 text-primary-600" />
              </div>
              <h3 className="font-subheading text-2xl font-semibold text-neutral-900 mb-3">
                Bankruptcy & Consumer Debt
              </h3>
              <p className="text-neutral-600 mb-6 leading-relaxed">
                Chapter 7 and Chapter 13 bankruptcy for individuals and businesses.
                Debt collection defense and consumer protection.
              </p>
              <ul className="space-y-2 mb-8">
                {['Chapter 7 & 13 Bankruptcy', 'Consumer debt relief', 'Debt collection defense', 'Personal & business debts'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-neutral-600">
                    <div className="w-1.5 h-1.5 bg-primary-400 rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/consumer-law"
                className="inline-flex items-center gap-2 font-subheading font-medium text-primary-600 hover:gap-3 transition-all"
              >
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-neutral-50">
        <div className="container-main">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-semibold text-neutral-900"
            >
              Why Clients Choose Beltas Law
            </motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: 'Personal Attention',
                description: 'As a sole practitioner, your case gets my full focus and dedication. You work directly with me, not a team of associates.',
              },
              {
                icon: MessageCircle,
                title: 'Clear Communication',
                description: "No legalese. I explain everything in plain language so you always understand where you stand and what your options are.",
              },
              {
                icon: FileCheck,
                title: 'Results-Focused',
                description: 'Every strategy is designed with your best outcome in mind. I fight for the results you deserve.',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="font-subheading text-xl font-semibold text-neutral-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-neutral-600 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-semibold text-neutral-900"
            >
              How We Work Together
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="mt-4 text-lg text-neutral-600"
            >
              A simple, straightforward process to get you the help you need.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connector line (desktop only) */}
            <div className="hidden md:block absolute top-8 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-primary-300 via-primary-300 to-primary-300" style={{ backgroundSize: '12px 2px' }} />

            {[
              { step: 1, title: 'Review Facts & Documents', description: 'A focused review of your situation, gathering all relevant facts and documentation.' },
              { step: 2, title: 'Explain Rights & Options', description: 'An explanation of your legal rights, risks, and realistic outcomes for your specific case.' },
              { step: 3, title: 'Strategy & Action', description: 'A tailored strategy for negotiation or litigation, with clear communication about next steps and costs.' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="text-center relative"
              >
                <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-heading text-2xl font-semibold relative z-10">
                  {item.step}
                </div>
                <h3 className="font-subheading text-xl font-semibold text-neutral-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-neutral-600 leading-relaxed max-w-xs mx-auto">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/contact" className="btn-primary">
              Schedule Your Initial Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-primary-50">
        <div className="container-main">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-2xl mx-auto mb-12"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-semibold text-neutral-900"
            >
              Client Experiences
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="mt-4 text-lg text-neutral-600"
            >
              These comments reflect the experiences of specific clients from public reviews.
            </motion.p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-12">
            {[
              {
                quote: "We used Brandy Beltas for a tenant dispute which got quite hostile. She was a staunch defender and advocate throughout the entire process and handled things beautifully.",
                context: "Tenant Dispute",
              },
              {
                quote: "Brandy made herself available last minute as well as over the weekend and provided excellent insights and advice concerning how to effectively deal with a very unexpected lawsuit with a very tight timeline.",
                context: "Emergency Matter",
              },
              {
                quote: "Brandy was truly amazing. She, along with her team, were professional, fast, attentive and detail oriented to resolve the matter both quickly and efficiently.",
                context: "Housing Case",
              },
              {
                quote: "She was able to help us settle the matter quickly and efficiently, and was very detail‑oriented in spotting a problem with a letter we got from our landlord when others weren't.",
                context: "Landlord-Tenant",
              },
              {
                quote: "Not only did her work result in a favorable outcome in my case, but she is also genuine and caring in the process.",
                context: "Referral Client",
              },
              {
                quote: "Brandy got back to me quickly and talked me through all my options in a way that would most quickly and cost-effectively resolve the dispute.",
                context: "Housing Dispute",
              },
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm"
              >
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-neutral-700 italic leading-relaxed mb-4">
                  "{testimonial.quote}"
                </p>
                <p className="text-sm text-neutral-500 font-subheading">
                  — {testimonial.context}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Review Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <a
              href={reviewLinks.yelp}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white rounded-full shadow-sm border border-neutral-200 font-subheading text-sm font-medium text-neutral-700 hover:border-primary-300 hover:shadow-md transition-all"
            >
              <svg className="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.14 11.28c-.21-.4-.62-.65-1.08-.64-.45 0-.86.26-1.06.66l-3.97 7.64a1.2 1.2 0 00.43 1.51c.4.27.93.26 1.32-.02l6.96-4.84c.39-.27.54-.78.36-1.22-.17-.44-.6-.72-1.06-.7-.13 0-.26.03-.38.08l-1.52-2.47z"/>
              </svg>
              Read All Yelp Reviews
              <ExternalLink className="w-3 h-3" />
            </a>
            <a
              href={reviewLinks.google}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white rounded-full shadow-sm border border-neutral-200 font-subheading text-sm font-medium text-neutral-700 hover:border-primary-300 hover:shadow-md transition-all"
            >
              <Star className="w-4 h-4 text-yellow-500" />
              Google Reviews
              <ExternalLink className="w-3 h-3" />
            </a>
            <Link
              to="/videos"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white rounded-full shadow-sm border border-neutral-200 font-subheading text-sm font-medium text-neutral-700 hover:border-primary-300 hover:shadow-md transition-all"
            >
              <Youtube className="w-4 h-4 text-red-600" />
              Watch Videos
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Video Section - CTA to Videos Page */}
      <section className="section-padding bg-neutral-50">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Youtube className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              Free Legal Tips & Insights
            </h2>
            <p className="text-lg text-neutral-600 mb-8">
              Watch helpful videos about housing court basics, bankruptcy options, tenant harassment,
              and more. Learn about your rights before scheduling a consultation.
            </p>
            <Link
              to="/videos"
              className="btn-primary"
            >
              Watch Videos
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-primary-900 text-white">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              Ready to Protect Your Rights?
            </h2>
            <p className="text-xl text-primary-200 mb-8 max-w-2xl mx-auto">
              Don't face landlord disputes or consumer issues alone. Get experienced legal
              help today with an initial consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent-500 text-white font-subheading font-medium rounded-full hover:bg-accent-400 transition-colors"
              >
                Schedule Initial Consultation
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={`tel:${CONTACT_INFO.phone}`}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/30 text-white font-subheading font-medium rounded-full hover:bg-white/10 transition-colors"
              >
                Call {CONTACT_INFO.phoneDisplay}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-8 bg-neutral-100">
        <div className="container-main">
          <p className="text-sm text-neutral-500 text-center max-w-3xl mx-auto">
            <strong>Important Notice:</strong> This site and its content are for informational
            purposes only and do not constitute legal advice. Contacting The Beltas Law Firm
            or viewing this website does not create an attorney–client relationship. Prior
            results do not guarantee a similar outcome.
          </p>
        </div>
      </section>
    </>
  );
}
